// src/LandingPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import HeroSection from './HeroSection';
import { useAuth } from './AuthContext';

const PLATFORMS = [
  'Google', 'YouTube', 'TikTok', 'Instagram', 'Facebook',
  'Twitter / X', 'Reddit', 'Pinterest', 'LinkedIn', 'Snapchat',
  'Amazon', 'WordPress', 'GitHub', 'Dropbox', 'Vimeo',
  'Spotify', 'Etsy', 'eBay', 'Cloudflare', 'Twitch',
];

const FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Legally Structured',
    desc: 'Every notice includes all required elements under 17 U.S.C. § 512(c)(3) — automatically. No legal knowledge needed.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
        <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: '60-Second Flow',
    desc: 'Fill in the form, hit generate. Your ready-to-send notice is drafted, formatted, and downloadable in under a minute.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: '40+ Verified Agents',
    desc: 'Pre-loaded DMCA agent addresses from the US Copyright Office for every major platform. Verified. Always accurate.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Sign & Download PDF',
    desc: 'Draw or upload your signature. Resize it. Download a print-ready PDF — ready to send immediately.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Edit Before Sending',
    desc: 'The generated letter is fully editable inline. Tweak the language, add context, personalize — then download.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M14 14h7v7h-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Full History',
    desc: 'Every notice you generate is saved to your account. Access, re-download, or copy any past notice instantly.',
  },
];

const STEPS = [
  { num: '01', title: 'Describe Your Work', desc: 'Enter your name, work type, original URL, and where it\'s being infringed.' },
  { num: '02', title: 'AI Drafts the Notice', desc: 'Our AI writes a complete, legally-structured DMCA notice addressed to the platform\'s verified agent.' },
  { num: '03', title: 'Sign & Download', desc: 'Add your signature, resize it to fit, and download a PDF ready to send in seconds.' },
];

const HIW_STEPS = [
  {
    n: '01',
    title: 'Describe the Infringement',
    desc: 'Enter your name, the type of work stolen, the original URL where you published it, and the infringing URL where it appears.',
  },
  {
    n: '02',
    title: 'AI Writes Your Notice',
    desc: 'Our AI generates a complete, legally-structured 17 U.S.C. § 512 DMCA takedown — addressed to the verified agent for your platform.',
  },
  {
    n: '03',
    title: 'Edit, Sign & Send',
    desc: 'Review and edit the letter inline, draw or upload your signature, then download a print-ready PDF and send it directly to the platform.',
  },
];

const PLANS = [
  {
    badge: 'Free',
    badgeClass: 'lp-plan-badge-free',
    name: 'Free',
    price: '$0',
    period: '/ month',
    desc: 'Try it once. No account needed, no card required.',
    notices: '2',
    features: [
      { text: 'AI-generated DMCA letter', on: true },
      { text: '40+ verified platform agents', on: true },
      { text: 'PDF download', on: true },
      { text: 'Draw / upload signature', on: true },
      { text: 'Edit letter inline', on: true },
      { text: 'Notice history', on: false },
      { text: 'Copy to clipboard', on: false },
    ],
    btnText: 'Get started free',
    btnClass: 'lp-plan-btn-outline',
    featured: false,
  },
  {
    badge: 'Most popular',
    badgeClass: 'lp-plan-badge-pop',
    name: 'Pro',
    price: '$9',
    period: '/ month',
    desc: 'For creators dealing with repeat infringement across platforms.',
    notices: '20',
    features: [
      { text: 'Everything in Starter', on: true },
      { text: 'Full notice history', on: true },
      { text: 'Copy to clipboard', on: true },
      { text: 'Resizable signature in PDF', on: true },
      { text: 'Re-download past notices', on: true },
    ],
    btnText: 'Start Pro — $9/mo',
    btnClass: 'lp-plan-btn-primary',
    featured: true,
  },
  {
    badge: 'Power user',
    badgeClass: 'lp-plan-badge-power',
    name: 'Unlimited',
    price: '$19',
    period: '/ month',
    desc: 'No caps. For photographers, musicians, studios filing constantly.',
    notices: 'Unlimited',
    features: [
      { text: 'Everything in Pro', on: true },
      { text: 'Unlimited notices', on: true },
      { text: 'Priority support', on: true },
    ],
    btnText: 'Go Unlimited — $19/mo',
    btnClass: 'lp-plan-btn-outline',
    featured: false,
  },
];

const FAQS = [
  {
    q: 'Is this a real legal document?',
    a: 'The notices are structured to comply with 17 U.S.C. § 512(c)(3) requirements. However, this tool does not constitute legal advice. For complex cases, consult an attorney.',
  },
  {
    q: 'What platforms are pre-loaded?',
    a: 'Google, YouTube, TikTok, Instagram, Facebook, Twitter/X, Reddit, Pinterest, LinkedIn, Snapchat, Amazon, WordPress, GitHub, Dropbox, Vimeo, Spotify, Etsy, eBay, Cloudflare, Twitch, Discord, and more — 40+ verified agents total.',
  },
  {
    q: 'What if the platform isn\'t in your list?',
    a: 'For unlisted platforms the notice will include editable placeholders for the recipient address. You can look up the address and fill it in before sending.',
  },
  {
    q: 'Can I edit the generated letter?',
    a: 'Yes — every letter has an inline edit mode. Change wording, add context, or personalise the tone before downloading your PDF.',
  },
  {
    q: 'Is my data saved?',
    a: 'Signed-in users get full history — every notice you\'ve generated is stored and accessible from the sidebar at any time.',
  },
];

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <svg className="faq-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="faq-answer"><p>{a}</p></div>
    </div>
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function Cross() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2, opacity: 0.28 }}>
      <path d="M18 6L6 18M6 6l12 12" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, showAuthModal } = useAuth();

  const [featRef, featIn]       = useInView(0.1);
  const [hiwRef,  hiwIn]        = useInView(0.08);
  const [stepsRef, stepsIn]     = useInView(0.1);
  const [pricingRef, pricingIn] = useInView(0.08);
  const [ctaRef, ctaIn]         = useInView(0.1);
  const [faqRef, faqIn]         = useInView(0.1);

  const handleGenerate = () => {
    if (!user) {
      showAuthModal('login');
      return;
    }
    navigate('/generate');
  };

  return (
    <div className="lp-root">

      {/* ── HERO ── */}
      <HeroSection onGenerate={handleGenerate} />

      {/* ── PLATFORM TICKER ── */}
      <div className="lp-ticker-wrap">
        <div className="lp-ticker-fade lp-ticker-fade-left" />
        <div className="lp-ticker">
          <div className="lp-ticker-track">
            {[...PLATFORMS, ...PLATFORMS].map((p, i) => (
              <span key={i} className="lp-ticker-item">{p}</span>
            ))}
          </div>
        </div>
        <div className="lp-ticker-fade lp-ticker-fade-right" />
      </div>

      {/* ── FEATURES ── */}
      <section className="lp-section lp-features" ref={featRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${featIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">Everything you need</div>
            <h2 className="lp-section-title">Built for content creators</h2>
          </div>
          <div className={`lp-features-grid ${featIn ? 'visible' : ''}`}>
            {FEATURES.map((f, i) => (
              <div className="lp-feature-card" key={i} style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS (two-column) ── */}
      <section className="lp-section lp-hiw-section" ref={hiwRef}>
        <div className="lp-section-inner lp-hiw-inner">

          {/* LEFT — steps */}
          <div className={`lp-hiw-left ${hiwIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">How it works</div>
            <h2 className="lp-section-title lp-hiw-title">
              End-to-end.<br /><em>Under 60 seconds.</em>
            </h2>

            <div className="lp-hiw-steps">
              {HIW_STEPS.map((s, i) => (
                <div key={s.n} className="lp-hiw-step" style={{ animationDelay: hiwIn ? `${i * 0.12}s` : '0s' }}>
                  <div className="lp-hiw-step-num">{s.n}</div>
                  <div className="lp-hiw-step-body">
                    <div className="lp-hiw-step-title">{s.title}</div>
                    <div className="lp-hiw-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — video */}
          <div className={`lp-hiw-right ${hiwIn ? 'visible' : ''}`}>
            <div className="lp-video-frame">
              <video
                src="https://res.cloudinary.com/dveak0t97/video/upload/v1775283192/Claimzer_qhb8jt.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            </div>
          </div>

        </div>
      </section>

      {/* ── SIMPLE 3-STEP LIST ── */}
      <section className="lp-section lp-steps-section" ref={stepsRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${stepsIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">How it works</div>
            <h2 className="lp-section-title">Three steps.<br /><em>One minute.</em></h2>
          </div>
          <div className={`lp-steps-list ${stepsIn ? 'visible' : ''}`}>
            {STEPS.map((s, i) => (
              <div className="lp-step-item" key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="lp-step-item-num">{s.num}</div>
                <div className="lp-step-item-connector" />
                <div className="lp-step-item-content">
                  <h3 className="lp-step-item-title">{s.title}</h3>
                  <p className="lp-step-item-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="lp-section lp-pricing" ref={pricingRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${pricingIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">Pricing</div>
            <h2 className="lp-section-title">Simple, honest<br /><em>pricing.</em></h2>
            <p className="lp-pricing-sub">No hidden fees. No credit card for Starter. Cancel any time.</p>
          </div>

          <div className={`lp-plans-grid ${pricingIn ? 'visible' : ''}`}>
            {PLANS.map((plan, i) => (
              <div
                key={i}
                className={`lp-plan-card ${plan.featured ? 'lp-plan-featured' : ''}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <span className={`lp-plan-badge ${plan.badgeClass}`}>{plan.badge}</span>
                <div className="lp-plan-name">{plan.name}</div>
                <div className="lp-plan-price">
                  {plan.price}
                  <span className="lp-plan-period">{plan.period}</span>
                </div>
                <p className="lp-plan-desc">{plan.desc}</p>
                <div className="lp-plan-pill">
                  <span className="lp-plan-pill-label">Notices / month</span>
                  <span className="lp-plan-pill-val">{plan.notices}</span>
                </div>
                <div className="lp-plan-divider" />
                <ul className="lp-plan-feats">
                  {plan.features.map((feat, j) => (
                    <li key={j} className={`lp-plan-feat ${feat.on ? '' : 'lp-plan-feat-off'}`}>
                      {feat.on ? <Check /> : <Cross />}
                      {feat.text}
                    </li>
                  ))}
                </ul>
                <button
                  className={`lp-plan-btn ${plan.btnClass}`}
                  onClick={() => navigate('/pricing-plans')}
                >
                  {plan.btnText}
                </button>
              </div>
            ))}
          </div>

          <div className={`lp-pricing-notes ${pricingIn ? 'visible' : ''}`}>
            <span>✓ No card for Starter</span>
            <span>✓ Cancel any time</span>
            <span>✓ Month-to-month</span>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section className="lp-cta-band" ref={ctaRef}>
        <div className={`lp-cta-band-inner ${ctaIn ? 'visible' : ''}`}>
          <div className="lp-cta-band-glow" />
          <h2 className="lp-cta-band-title">
            Your work deserves<br /><em>protection.</em>
          </h2>
          <p className="lp-cta-band-sub">
            Stop letting infringers get away with it. Generate your first notice free —
            no account required.
          </p>
          <button className="lp-btn-primary lp-btn-lg" onClick={handleGenerate}>
            Generate my DMCA notice
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="lp-cta-chips">
            <span>✓ PDF download</span>
            <span>✓ 40+ verified platforms</span>
            <span>✓ Sign in-app</span>
            <span>✓ 17 U.S.C. § 512 compliant</span>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section lp-faq" ref={faqRef}>
        <div className="lp-section-inner lp-faq-inner">
          <div className={`lp-section-header ${faqIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">FAQ</div>
            <h2 className="lp-section-title">Common questions</h2>
          </div>
          <div className={`lp-faq-list ${faqIn ? 'visible' : ''}`}>
            {FAQS.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

    </div>
  );
}